import { useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/lib/utils/classnames';
import type { Block, RowBlock as RowBlockType, VideoInfoBlock } from '@/shared/types/blocks';
import { BlockRenderer } from '../BlockRenderer';

/**
 * 서버에서 columns 형식으로 보낼 때의 컬럼 타입
 */
interface RowColumn {
  id: string;
  span?: number;
  order?: number;
  /** 개별 컬럼 확장 여부 (true면 flex-1로 나머지 공간 채움) */
  expand?: boolean;
  content: Block;
}

interface RowBlockProps extends Omit<RowBlockType, 'type' | 'id'> {
  /** 서버에서 columns 형식으로 보낼 경우 지원 */
  columns?: RowColumn[];
  /** 고정 높이 (px) - Figma 디자인에서 비디오+스크립트 영역 */
  height?: number;
  /** 아이템 확장 여부 (false면 자연스러운 너비 유지) */
  expand?: boolean;
  onToolClick?: (toolId: string) => void;
  /** 외부에서 주입된 연결 영상 ID (타임스탬프 배지 클릭용) */
  linkedVideoId?: string;
}

/**
 * RowBlock - 수평 배치 컨테이너
 *
 * Figma 디자인 기준:
 * - 비디오 + 스크립트 2컬럼 레이아웃: 50:50, 높이 동기화
 * - video-info + transcript 조합일 때 JS로 높이 동기화
 */
export function RowBlock({
  items,
  columns,
  justify = 'start',
  align = 'stretch',
  gap = 24,
  height,
  expand = false,
  onToolClick,
}: RowBlockProps) {
  const firstColRef = useRef<HTMLDivElement>(null);
  const [syncedHeight, setSyncedHeight] = useState<number | null>(null);

  const justifyClass = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
    between: 'justify-between',
    around: 'justify-around',
  }[justify];

  const alignClass = {
    start: 'items-start',
    end: 'items-end',
    center: 'items-center',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
  }[align];

  // columns 형식이면 정렬 후 expand 정보와 함께 유지
  const resolvedColumns: { content: Block; expand: boolean }[] = columns
    ? columns
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((col) => ({ content: col.content, expand: col.expand ?? expand }))
    : (items || []).map((item) => ({ content: item, expand }));

  // video-info + transcript 조합 감지
  const firstContent = resolvedColumns[0]?.content;
  const secondContent = resolvedColumns[1]?.content;
  const isVideoTranscriptLayout =
    resolvedColumns.length === 2 &&
    firstContent?.type === 'video-info' &&
    secondContent?.type === 'transcript';

  // video-info + transcript 레이아웃에서 videoId 추출
  const linkedVideoId = isVideoTranscriptLayout
    ? (firstContent as VideoInfoBlock).videoId
    : undefined;

  // 첫 번째 컬럼 높이 측정 및 동기화
  useEffect(() => {
    if (!isVideoTranscriptLayout || !firstColRef.current) return;

    const updateHeight = () => {
      if (firstColRef.current) {
        setSyncedHeight(firstColRef.current.offsetHeight);
      }
    };

    // 초기 측정
    updateHeight();

    // ResizeObserver로 크기 변화 감지
    const observer = new ResizeObserver(updateHeight);
    observer.observe(firstColRef.current);

    return () => observer.disconnect();
  }, [isVideoTranscriptLayout]);

  return (
    <div
      className={cn('flex w-full', justifyClass, alignClass)}
      style={{
        gap: gap ? `${gap}px` : undefined,
        height: height ? `${height}px` : undefined,
      }}
    >
      {resolvedColumns.map((col, index) => {
        const isFirstCol = index === 0;
        const isSecondColWithSync = index === 1 && isVideoTranscriptLayout && syncedHeight;
        // 비디오-자막 레이아웃: 첫 번째 컬럼도 self-start로 자연스러운 높이 유지
        const isFirstColInSync = isFirstCol && isVideoTranscriptLayout;

        return (
          <div
            key={col.content.id}
            ref={isFirstColInSync ? firstColRef : undefined}
            className={cn(
              col.expand && 'flex-1',
              'min-w-0 flex flex-col',
              // 비디오 컬럼: self-start로 items-stretch 무시 (자연 높이 측정용)
              isFirstColInSync && 'self-start',
              // 자막 컬럼: overflow-hidden + self-start로 높이 고정
              isSecondColWithSync && 'overflow-hidden self-start',
            )}
            style={isSecondColWithSync ? { height: `${syncedHeight}px` } : undefined}
          >
            <BlockRenderer
              blocks={[col.content]}
              onToolClick={onToolClick}
              className={isSecondColWithSync ? 'h-full [&>*]:h-full' : 'flex-1'}
              linkedVideoId={isSecondColWithSync ? linkedVideoId : undefined}
            />
          </div>
        );
      })}
    </div>
  );
}
